import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import { Divider, FormControl, InputLabel, Select, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { NumberFormatterOptions } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { FormatType } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { CurrencyDimensionMeta } from "../../../../../../../api/types/svTypes.ts";
import { getAllNumberFormattersDescription } from "../../../formatters/NumberFormatter.ts";
import { selectCurrencyDimensions } from "../../../store/selectors/metaDataSelectors.ts";
import MeasureCustomFormatPopup from "../pivot/table/format/custom/MeasureCustomFormatPopup.tsx";

const formatterTypes = {
  Custom: "Custom",
  Default: "Default",
};

interface Props {
  format: NumberFormatterOptions | undefined | null;
  label?: string;
  currencyDimensions?: CurrencyDimensionMeta[];
  onUpdateFormatter: (formatter: NumberFormatterOptions | undefined) => void;
  disabled: boolean;
}

export const FormatSelect = ({ format, label, currencyDimensions, onUpdateFormatter, disabled }: Props) => {
  const [selectedOptionsType, setSelectedOptionsType] = useState(format?.type || "Default");
  const [openCustomFormat, setOpenCustomFormat] = useState(false);
  const [customFormatOptions, setCustomFormatOptions] = useState<NumberFormatterOptions>({} as NumberFormatterOptions);
  const storeCurrencyDimensions = useSelector(selectCurrencyDimensions);
  const effectiveCurrencyDimensions = currencyDimensions ?? storeCurrencyDimensions;

  const allFormatters = useMemo(() => getAllNumberFormattersDescription(), []);

  const onFormatChanged = (value: string) => {
    if (value === formatterTypes.Custom) {
      return;
    }
    const options = getFormatOptionsByType(value);
    const format = options?.options as NumberFormatterOptions;
    setSelectedOptionsType(value);
    onUpdateFormatter(format && { ...format });
  };

  const renderSelectedValue = (value: string) => {
    let valueToRender = value;
    if (value !== formatterTypes.Custom && value !== formatterTypes.Default) {
      const description = allFormatters.find((formatter) => formatter.options.type === value);
      if (description) {
        valueToRender = description.title;
      }
    }

    return (
      <Typography variant="caption" sx={{ flex: 1 }}>
        {valueToRender}
      </Typography>
    );
  };

  const getFormatOptionsByType = (type: string) => {
    return allFormatters.find((formatter) => formatter.options.type === type);
  };

  const openCustomFormatPopup = () => {
    if (format?.type === formatterTypes.Custom) {
      setCustomFormatOptions({ ...format });
    } else {
      setCustomFormatOptions({
        type: formatterTypes.Custom,
        formatType: FormatType.Numeric,
        decimals: 2,
      } as NumberFormatterOptions);
    }
    setOpenCustomFormat(true);
  };

  const applyCustomFormat = (options: NumberFormatterOptions) => {
    setSelectedOptionsType(formatterTypes.Custom);
    onUpdateFormatter({ ...options, formatType: options.formatType || FormatType.Numeric });
  };

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setSelectedOptionsType(format?.type || "Default");
  }, [format?.type]);

  return (
    <>
      <FormControl sx={{ flex: 1 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          sx={{
            display: "flex",
            flex: 1,
            "& .MuiTypography-root": { fontSize: 13 },
            "& .MuiSelect-icon": { color: "secondary.main" },
          }}
          renderValue={renderSelectedValue}
          value={selectedOptionsType}
          onChange={(evt) => onFormatChanged(evt.target.value)}
          disabled={disabled}
        >
          <MenuItemStyled
            value={formatterTypes.Custom}
            selected={selectedOptionsType === formatterTypes.Custom}
            onClick={() => openCustomFormatPopup()}
          >
            <Typography>Custom Format</Typography>
          </MenuItemStyled>
          <Divider />
          <MenuItemStyled selected={selectedOptionsType === formatterTypes.Default} value={formatterTypes.Default}>
            <Typography>Default</Typography>
          </MenuItemStyled>
          {allFormatters.map((description) => (
            <MenuItemStyled
              key={description.title}
              sx={{ display: "flex", flex: 1 }}
              selected={selectedOptionsType === description.options.type}
              value={description.options.type}
            >
              <Typography sx={{ flex: 1 }}>{description.title}</Typography>
              <Typography sx={{ color: "rgba(33, 33, 33, 0.38)", minWidth: "120px", textAlign: "end", pl: "1rem" }}>
                {description.subTitle}
              </Typography>
            </MenuItemStyled>
          ))}
        </Select>
      </FormControl>
      {openCustomFormat && (
        <MeasureCustomFormatPopup
          formatOptions={customFormatOptions}
          currencyDimensions={effectiveCurrencyDimensions}
          applyFormat={applyCustomFormat}
          close={() => setOpenCustomFormat(false)}
        />
      )}
    </>
  );
};

export default FormatSelect;
